import React, { useEffect, useMemo, useState } from 'react';
import { IconSuccess16, ISuggestionItem } from '@cian/ui-kit';
import { ISuggestionDefaultItem } from '@cian/ui-kit/suggest/types/ISuggestProps';
import { IEFieldSource, IField } from '../types/calculator';
import { useBaseField } from '../components/BaseField';

const _findSuggestItemLabelByValue = (value: string | undefined, source: IEFieldSource[] = []) => {
  if (!value) {
    return '';
  }

  const item = source.find(item => item.value === value);

  return item ? item.label : '';
};

export const useSuggestField = (props: IField) => {
  const { field, fieldState } = useBaseField(props);

  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [inputValue, setInputValue] = useState(_findSuggestItemLabelByValue(String(field.value), props.source));

  useEffect(() => {
    setInputValue(_findSuggestItemLabelByValue(String(field.value), props.source));
  }, [props.source, field.value]);

  const handleSuggestSelect = (item: ISuggestionItem<string>) => {
    field.onChange(item.value);
    setInputValue(item.title);
    setIsSuggestOpen(false);
  };

  const handleSuggestClose = () => {
    setIsSuggestOpen(false);
    setInputValue(_findSuggestItemLabelByValue(String(field.value), props.source));
  };

  const handleInputFocus = () => {
    setIsSuggestOpen(true);
    setInputValue('');
  };

  const handleInputChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setInputValue(value);
    if (!isSuggestOpen) {
      setIsSuggestOpen(true);
    }
  };

  const suggestions: ISuggestionDefaultItem<string>[] = useMemo(() => {
    if (!props.source) {
      return [];
    }

    return props.source
      .filter(item => item.label.toLowerCase().includes(inputValue.toLowerCase()))
      .map(item => ({
        value: item.value,
        title: item.label,
        type: 'default',
        rightIcon: item.value === field.value ? <IconSuccess16 color="decorative__theme__blue" /> : undefined,
      }));
  }, [props.source, inputValue, field.value]);

  return {
    inputValue,
    error: fieldState.error,
    isError: fieldState.isDirty || fieldState.invalid,
    name: field.name,
    label: props.label,
    placeholder: props.placeholder,
    disabled: props.disabled,
    isSuggestOpen,
    handleSuggestSelect,
    handleSuggestClose,
    handleInputFocus,
    handleInputChange,
    suggestions,
  };
};
